import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Plus } from 'lucide-react';
import { openPokeAgent } from '../lib/openPokeAgent';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { logPaywallEvent } from '../utils/paywallAnalytics';
import './FigChat.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';


const FigChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasDoneOnboarding } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // CRITICAL: Initialize with null ID - will be set to authenticated user ID in initSession
  // Using a random ID causes resume/roadmap mismatches
  const [userProfile, setUserProfile] = useState({
    id: null,
    verified: false,
    summary: ''
  });
  const hasInitialized = useRef(false);
  const fileInputRef = useRef(null);
  const isSubmitting = useRef(false); // Prevent duplicate submissions
  const chatStartTimeRef = useRef(Date.now()); // For fig_chat_completed duration

  // Cleanup on unmount - cancel pending requests
  useEffect(() => {
    return () => {
      openPokeAgent.cancel();
    };
  }, []);

  useEffect(() => {
    // Robust check: If user hits back button but is already "done" according to server, bounce them forward
    const checkServerStatus = async () => {
        // Skip check if we are explicitly entering from a valid flow
        if (location.state?.fromOnboarding || location.state?.fromGmailSync) {
            return;
        }

        if (!user) return;
        
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            if (!token) return;

            // 1. Check strict onboarding status source of truth
            const statusResponse = await fetch(`${API_BASE}/api/user/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (statusResponse.ok) {
                const status = await statusResponse.json();
                if (status.has_done_onboarding) {
                    
                    // 2. Get the roadmap to redirect to
                    const rmResponse = await fetch(`${API_BASE}/api/roadmaps/latest`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    
                    if (rmResponse.ok) {
                        const result = await rmResponse.json();
                        if (result.roadmap && result.roadmap.id) {
                            navigate(`/roadmap/${result.roadmap.id}`, { replace: true });
                            return;
                        }
                    }
                    // No roadmap exists (e.g. user deleted it) — stay on FigChat to create a new one
                }
            }
        } catch (e) {
            console.warn("Status check failed", e);
        }
    };
    
    checkServerStatus();
  }, [user, navigate]);

  useEffect(() => {
    // Only redirect if we are NOT coming from the Gmail sync flow, Onboarding flow, or forced back
    if (hasDoneOnboarding && !location.state?.fromGmailSync && !location.state?.fromOnboarding && !location.state?.forceChat) {
      navigate('/roadmap');
    }
  }, [hasDoneOnboarding, navigate, location.state]);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Mark where this paywall session came from so PaywallPage can read it reliably
    sessionStorage.setItem('paywall_source', 'fig_chat');
    chatStartTimeRef.current = Date.now();
    logPaywallEvent('fig_chat_started', {});

    // Sync with Supabase user and Resume Data
    const initSession = async () => {
      // Add a small delay to prevent blocking initial render
      await new Promise(resolve => setTimeout(resolve, 100));
      // 1. Try Location State First (Immediate Handoff from Onboarding)
      let resumeData = location.state?.resumeData || {};
      let youtubeData = location.state?.resumeData?.youtube || null;

      // 2. Try Supabase (Persistent Source) - No localStorage to avoid freeze issues
      if (Object.keys(resumeData).length === 0) {
         try {
            if (user) {
              // Authenticated: query by user ID
              const { data, error } = await supabase
                  .from('user_resumes')
                  .select('*')
                  .eq('user_id', user.id)
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .single();

              if (data?.extracted_data) {
                  resumeData = data.extracted_data;
                  if (resumeData.youtube) {
                      youtubeData = resumeData.youtube;
                  }
              } else if (data?.resume_data) {
                  resumeData = data.resume_data;
              }
            } else {
              // Not authenticated: load most recent resume via backend API (dev mode)
              const res = await axios.get(`${API_BASE}/api/latest-resume`);
              if (res.data?.extracted_data) {
                  resumeData = res.data.extracted_data;
                  if (resumeData.youtube) {
                      youtubeData = resumeData.youtube;
                  }
              }
            }
         } catch (err) {
         }
      }

      // Ensure YouTube data is included in resume data for the agent
      if (youtubeData && !resumeData.youtube) {
          resumeData.youtube = youtubeData;
      }

      // 3. Get Scraped Profile (Email Context) - Only from location state, no localStorage
      const scrapedProfile = location.state?.scrapedProfile || null;
      
      let firstName = 'friend';
      
      // Name Priority: Resume > Google Profile > Email > Fallback
      if (resumeData?.full_name) {
          firstName = resumeData.full_name.split(' ')[0];
      } else if (user?.user_metadata?.full_name) {
          firstName = user.user_metadata.full_name.split(' ')[0];
      } else if (user?.email) {
          firstName = user.email.split('@')[0]; // Simple fallback
      }

      let profile = {
        id: user?.id || 'user_' + Math.random().toString(36).substr(2, 9),
        email: user?.email,
        first_name: firstName,
        resume: resumeData,
        scraped: scrapedProfile, // Passed to agent as "world_model" usually
        verified: false,
        summary: ''
      };
      
      // Profile initialized - logging removed for performance
      
      setUserProfile(profile);

      // Start typing immediately (no hardcoded "yo, i'm fig")
      setIsTyping(true);

      const generateIntro = async () => {
        try {
          // Trigger the agent to generate the observation based on the profile
          // We send a hidden system-like message to prompt the agent
          let triggerMsg = "I'm new here. Who are you?";
          
          // Re-evaluate emptiness because variable scopes might be tricky in async closures
          const hasResume = profile.resume && Object.keys(profile.resume).length > 0;
          const hasScraped = profile.scraped && Object.keys(profile.scraped).length > 0;
          
          if (hasScraped && hasResume) {
            triggerMsg = "Analyze my digital footprint AND my resume. Connect the dots and give me a cold read.";
          } else if (hasScraped) {
            triggerMsg = "Analyze my digital footprint and give me a cold read.";
          } else if (hasResume) {
            triggerMsg = "I just uploaded my resume. Analyze it and tell me who I am.";
          }
          
          // Add timeout to prevent hanging (45 seconds max - matches axios timeout)
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), 45000)
          );

          const reply = await Promise.race([
            openPokeAgent.handle(triggerMsg, profile, messages),
            timeoutPromise
          ]);
          
          // Clear initial typing state before playing the message animation
          setIsTyping(false);

          // Use the same sequential message function to handle bubblebreak and newlines
          await addAgentMessagesSequential(reply);
        } catch (error) {
          console.error("Failed to generate intro:", error);
          setIsTyping(false);
          const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
          const timeoutError = error.message === 'Request timeout'
            ? "Taking too long to respond. Please try again."
            : `My brain is offline (${errorMessage}). Drop a resume anyway.`;
          setMessages(prev => [...prev, { role: 'agent', content: timeoutError }]);
        }
      };

      // Start after a short delay
      setTimeout(generateIntro, 1000);
    };
    initSession();
  }, [user]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 1. Show user message "Uploading [filename]..."
    const uploadMsg = { role: 'user', content: `📄 Uploading ${file.name}...` };
    setMessages(prev => [...prev, uploadMsg]);
    setIsTyping(true);

    // CRITICAL: Use authenticated user ID for resume upload
    const userId = user?.id || userProfile.id;
    if (!userId) {
      setMessages(prev => [...prev, { role: 'agent', content: "Hmm, you need to be logged in to upload a resume. Try refreshing?" }]);
      setIsTyping(false);
      return;
    }

    // Get auth token for secure upload
    let authHeaders = { 'Content-Type': 'multipart/form-data' };
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        authHeaders['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch (e) {
      console.warn("Could not get auth token:", e);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      // 2. Upload to backend with auth
      const response = await axios.post(`${API_BASE}/api/upload-resume?user_id=${encodeURIComponent(userId)}`, formData, {
        headers: authHeaders
      });

      // 3. Update local state with resume data
      const extractedData = response.data;
      // Note: Backend already saves to Supabase user_resumes
      
      const updatedProfile = { ...userProfile, resume: extractedData };
      setUserProfile(updatedProfile);

      // Mark the upload message as read by adding a dummy agent action or just relying on the next response
      // But to be explicit about "Read" status appearing on the upload message:
      // The "Read" status appears on the last user message if there are subsequent messages or if we force it.
      // Our current logic: {messages.length > 0 && messages[messages.length - 1].role === 'user' && ...}
      // This shows "Read" immediately. 
      // To make it feel like "After the resume is dropped, the message should be marked as read",
      // we can just proceed to the agent response which will push the user message up.
      
      // 4. Trigger Agent with "Resume uploaded" context
      const triggerText = "I just uploaded my resume. What's the vibe?";
      
      const reply = await openPokeAgent.handle(triggerText, updatedProfile, messages);
      await addAgentMessagesSequential(reply);
      checkNavigation(reply, updatedProfile);

    } catch (error) {
      console.error("Upload failed:", error);
      setMessages(prev => [...prev, { role: 'agent', content: "Oof, that upload failed. Try a different file?" }]);
    } finally {
      setIsTyping(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      // Reuse the existing upload logic
      // Create a synthetic event object
      const syntheticEvent = { target: { files: [file] } };
      handleFileUpload(syntheticEvent);
    }
  };

  const saveConfirmedProfile = async (profile) => {
    try {
      // Construct form data from the profile/resume
      // We also want to include the chat history so the backend can refine the profile
      // based on user corrections.
      const chatHistory = messages.map(m => `${m.role}: ${m.content}`).join('\n');

      const formData = {
        skills: profile.resume?.skills?.join(', ') || profile.resume?.all_skills?.join(', ') || '',
        interests: profile.resume?.interests?.join(', ') || '',
        experiences: profile.resume?.summary || '',
        roleModels: '',
        country: 'United States',
        source: 'open_poke_chat',
        chat_context: chatHistory, // Pass chat history string for valid JSON
        conversation: messages, // Pass structured messages for the AI
        world_model: profile.scraped?.world_model || null, // Include World Model from Email Analysis
        resume_data: profile.resume || null // Pass full structured resume data
      };

      // CRITICAL: Refresh session before saving to handle long chat sessions
      // This prevents "token expired" errors after 30+ minute conversations
      let token = null;
      try {
        // First try to refresh the session
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.warn("Session refresh failed, trying existing session:", refreshError);
        }

        // Get the (potentially refreshed) session
        const { data: { session } } = await supabase.auth.getSession();
        token = session?.access_token;

        if (!token && user) {
          // Last resort: try to get a new session
          console.warn("No token found, user may need to re-authenticate");
        }
      } catch (authError) {
        console.error("Auth error during save:", authError);
      }

      if (token) {
        const response = await axios.post(`${API_BASE}/api/save-roadmap`, {
          title: `Verified Profile Roadmap - ${new Date().toLocaleDateString()}`,
          form_data: formData,
          career_paths: {}, // Initialize as empty object, will be generated on roadmap page
          role_model_research: {},
          non_linear_research: {}
        }, {
           headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.success) {
        }
      } else {
        console.warn("No auth token, skipping save to Supabase (dev mode).");
      }

      // Log fig_chat_completed — fire-and-forget, never blocks navigation
      const durationSeconds = Math.floor((Date.now() - chatStartTimeRef.current) / 1000);
      const userMessages = messages.filter(m => m.role === 'user');
      logPaywallEvent('fig_chat_completed', {
        message_count: messages.length,
        user_message_count: userMessages.length,
        duration_seconds: durationSeconds,
        had_role_models: !!(profile.resume?.role_models?.length || formData.roleModels),
        had_skills: !!(formData.skills),
      });

    } catch (error) {
      console.warn("Failed to save profile (non-blocking):", error.message);
    }
  };

  const checkNavigation = async (reply, currentProfile) => {
    // Check for the "Success Contract" phrase (case-insensitive and robust)
    const normalizedReply = reply.toLowerCase().replace(/[.,!]/g, '');
    const triggerPhrase = "cool i have enough to build a map";
    
    if (normalizedReply.includes(triggerPhrase)) {
      // Save the profile before navigating
      await saveConfirmedProfile(currentProfile);
      
      // longer delay before navigation (was 1500)
      setTimeout(() => {
        navigate('/roadmap');
      }, 3500); // 2 seconds longer
    }
  };

  const addAgentMessagesSequential = async (fullReply) => {
    // Check for [bubblebreak] delimiter first (two-bubble comedic timing)
    if (fullReply.includes('[bubblebreak]')) {
      const chunks = fullReply.split('[bubblebreak]').map(chunk => chunk.trim()).filter(chunk => chunk !== '');
      
      // Limit chunks to prevent too many rapid updates (max 5 chunks)
      const limitedChunks = chunks.slice(0, 5);
      if (chunks.length > 5) {
        // Combine remaining chunks into the last one
        limitedChunks[4] = chunks.slice(4).join(' ');
      }
      
      for (let i = 0; i < limitedChunks.length; i++) {
        setIsTyping(true);
        // Simulate typing delay (600ms - 1200ms)
        await new Promise(resolve => setTimeout(resolve, 600 + Math.random() * 600));
        
        setIsTyping(false);
        setMessages(prev => [...prev, { role: 'agent', content: limitedChunks[i] }]);
        
        // Comedic timing: shorter delay between bubbles (250-450ms) for punchline effect
        if (i < limitedChunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 250 + Math.random() * 200));
        }
      }
    } else {
      // For regular messages, don't split by newlines - just show as one message
      // This prevents hundreds of state updates that can freeze the page
      setIsTyping(true);
      // Simulate typing delay based on message length (min 800ms, max 2000ms)
      const delay = Math.min(800 + (fullReply.length / 10), 2000);
      await new Promise(resolve => setTimeout(resolve, delay));

      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'agent', content: fullReply }]);
    }

  };

  const handleAutoSend = async (text, profile) => {
    setMessages([{ role: 'user', content: text }]);
    setIsTyping(true);
    
    try {
      const reply = await openPokeAgent.handle(text, profile, messages);
      await addAgentMessagesSequential(reply);
      checkNavigation(reply, profile);
    } catch (error) {
      console.error("Agent error:", error);
    } finally {
      setIsTyping(false);
    }
  };
  
  const messagesEndRef = useRef(null);
  const startTime = useRef(new Date());

  const scrollToBottom = () => {
    // Use requestAnimationFrame to prevent blocking the main thread
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };

  // Debounce scroll to prevent excessive calls
  const scrollTimeoutRef = useRef(null);
  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      scrollToBottom();
    }, 100); // Debounce by 100ms
    
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, isTyping]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSubmitting.current) return;

    isSubmitting.current = true;
    const newMessage = { role: 'user', content: inputValue };
    const messageToSend = inputValue;

    // Update state immediately
    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setIsTyping(true);

    // Let React render the user message before starting the API call
    await new Promise(resolve => setTimeout(resolve, 0));

    try {
      // Add timeout to prevent hanging (45 seconds max - matches axios timeout)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 45000)
      );

      const reply = await Promise.race([
        openPokeAgent.handle(messageToSend, userProfile, messages),
        timeoutPromise
      ]);

      await addAgentMessagesSequential(reply);
      checkNavigation(reply, userProfile);
    } catch (error) {
      console.error("Agent error:", error);
      setIsTyping(false);
      // Don't show error for cancelled requests
      if (error.message === 'Request cancelled') return;
      const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
      const timeoutError = error.message === 'Request timeout'
        ? "Taking too long to respond. Please try again or refresh the page."
        : `Sorry, I'm having trouble connecting right now (${errorMessage}).`;
      setMessages(prev => [...prev, { role: 'agent', content: timeoutError }]);
    } finally {
      setIsTyping(false);
      isSubmitting.current = false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, ease: "easeOut" }}
      className={`fig-chat-container ${isDragging ? 'dragging' : ''}`}
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bluewhite.png)` }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-message">Drop Resume PDF Here 📄</div>
        </div>
      )}
      <div className="imessage-header">
        <div className="header-left">
          {/* Back icon removed */}
        </div>
        <div className="header-center">
          <div className="avatar-container">
            <div className="avatar-circle">
               <img src="/figlogo.png" alt="FIG" className="avatar-image" />
            </div>
          </div>
          <div className="contact-name">
            FIG <span className="arrow-right">›</span>
          </div>
        </div>
        <div className="header-right">
          {/* Placeholder for balance */}
        </div>
      </div>

      <div className="messages-area">
        {/* Spacer pushes messages toward the bottom when there are few */}
        <div className="messages-spacer" />

        <div className="timestamp-separator">
          Today {startTime.current.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </div>

        {messages.map((msg, index) => {
          const isLastUserMessage = msg.role === 'user' && index === messages.length - 1;

          return (
            <div key={index} className={`message-row ${msg.role}`}>
              <div className={`message-bubble ${msg.role}`}>
                {msg.content}
              </div>
              {isLastUserMessage && (
                <div className="read-receipt">Read {new Date().toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'})}</div>
              )}
            </div>
          );
        })}
        
        {isTyping && (
          <div className="message-row agent">
            <div className="message-bubble agent typing">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="input-area">
        <div className="input-wrapper">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="iMessage"
          />
          {inputValue.trim() && (
             <button className="send-btn" onClick={handleSendMessage}>
               <div className="send-arrow">↑</div>
             </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FigChat;
